import React, { useEffect, useState } from "react";
import Popup from "../../../components/common/model/Popup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  DialogContent,
  DialogContentText,
  Grid,
  Typography,
  Fade,
} from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import useStyles from "../../../components/common/model/styles";
import CardView from "../../../components/common/CardView";

import { getEmailTemplateAction } from "../../../store/reducers/templateSlice";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import ManageSendEmail from "./ManageSendEmail";
import {
  getAllEmailTemplatesAction,
  getLeadStageWithPlaceholdersAction,
} from "../../../store/reducers/emailTemplateSlice";

interface Template {
  id: number;
  template_name: string;
  is_primary?: boolean;
  template_content: string;
  lead_stage?: string | null;
}

interface ManageEmailTemplateProps {
  open: boolean;
  onHide: () => void;
  title: string;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<Template | null>>;
}

const ManageEmailTemplate: React.FC<ManageEmailTemplateProps> = ({
  open,
  onHide,
  title,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { allEmailTemplates, leadStageWithPlaceholders } = useSelector(
    (state: any) => state.emailTemplates,
  );
   const { leadDetails } = useSelector(
      (state: any) => state.lead,
    );

  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [openSendEmail, setOpenSendEmail] = useState(false);

  useEffect(() => {
    dispatch(getAllEmailTemplatesAction({ lead_stage: null }));
    dispatch(getLeadStageWithPlaceholdersAction());
    // dispatch(getEmailTemplateAction());
  }, [dispatch]);

  const handleExpansion = (panel: string) => {
    setExpanded((prev) => (prev === panel ? false : panel));
  };

  const handleClickTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setOpenSendEmail(true);
  };

  const closeHandlerView = () => {
    setSelectedTemplate(null);
    setOpenSendEmail(false);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  /*
  --------------------------------
  Sort Lead Stages (API Based Order)
  --------------------------------
  */
  const sortedLeadStages = [...(leadStageWithPlaceholders || [])].sort(
    (a, b) => a.stage_order - b.stage_order
  );

  /*
  --------------------------------
  Group Templates
  --------------------------------
  */
  const groupTemplates = (templates: Template[]) => {
    const grouped: Record<string, Template[]> = {};

    templates.forEach((template) => {
      const stageKey = template?.lead_stage || "OTHER";

      if (!grouped[stageKey]) {
        grouped[stageKey] = [];
      }

      grouped[stageKey].push(template);
    });

    return grouped;
  };

  const groupedTemplates = groupTemplates(allEmailTemplates || []);

  /*
  --------------------------------
  Ordered Groups (Dynamic)
  --------------------------------
  */
    /*
  --------------------------------
  Filter groups based on leadStatus
  --------------------------------
  */
  const getFilteredGroups = () => {
    // If leadStatus is "COMPLETED", only show COMPLETED section
    if (leadDetails?.lead?.status?.toUpperCase() === "COMPLETED") {
      const completedKey = leadStageWithPlaceholders?.find(
        (item: any) => item.stage.toUpperCase() === "COMPLETED"
      )?.stage;

      // If COMPLETED stage exists in groupedTemplates
      if (completedKey && groupedTemplates[completedKey]) {
        return [completedKey];
      }

      // Fallback: check if "COMPLETED" exists as a key
      const completedKeyFallback = Object.keys(groupedTemplates).find(
        (key) => key.toUpperCase() === "COMPLETED"
      );

      if (completedKeyFallback) {
        return [completedKeyFallback];
      }

      return [];
    }

    // Otherwise, return all groups
    return [
      ...sortedLeadStages
        .map((stage) => stage.stage)
        .filter((stage) => groupedTemplates[stage]),
      ...(groupedTemplates["OTHER"] ? ["OTHER"] : []),
    ];
  };

  const orderedGroups = getFilteredGroups();

  /*
  --------------------------------
  Get Stage Label (UI Friendly)
  --------------------------------
  */
const getStageLabel = (stageKey: string) => {
  if (stageKey === "OTHER") return "Other";

  const stageData = leadStageWithPlaceholders?.find(
    (item: any) => item.stage === stageKey
  );

  const label = stageData?.description || stageKey.replace(/_/g, " ");

  // ✅ Custom override
  if (
    label?.toLowerCase() === "lead created" ||
    stageKey === "NEW_LEAD"
  ) {
    return "New Lead";
  }

  return label;
};

 
  return (
    <>
      <Popup
        open={open}
        fullWidth
        title={title}
        onHide={onHide}
        maxWidth={"lg"}
        popupBg="#fff"
      >
        <DialogContent sx={{ paddingLeft: "20px" }}>
          <DialogContentText className={classes.formAddBox}>
            <Grid container spacing={2} sx={{ marginTop: "10px" }}>
              {!allEmailTemplates?.length ? (
                <Typography variant="h6" sx={{ margin: "auto" }}>
                  No Email Templates Found
                </Typography>
              ) : (
                orderedGroups.map((group, index) => (
                  <Grid item xs={12} key={index}>
                    <Accordion
                      expanded={expanded === group}
                      onChange={() => handleExpansion(group)}
                      TransitionProps={{ timeout: 400 }}
                      sx={{
                        boxShadow: "none",
                        borderTop: "2px solid #bebebe",
                        borderRadius: 0,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                        <Typography sx={{ fontWeight: 600,textTransform: "capitalize" }}>
                          {/* {group ==='NEW_LEAD' ? 'NEW LEAD' : group} */}
                          {getStageLabel(group)}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {groupedTemplates[group]?.map(
                            (template: Template, idx: number) => (
                              <Grid
                                item
                                xs={12}
                                md={4}
                                key={idx}
                                sx={{
                                  padding: "16px 10px",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleClickTemplate(template)}
                              >
                                <CardView
                                  {...{
                                    ...template,
                                    title: template.template_name,
                                    description: template.template_content,
                                    isEdit: false,
                                    isStar:template?.is_primary ? true : false,
                                    width: "100%",
                                  }}
                                />
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))
              )}
            </Grid>
          </DialogContentText>
        </DialogContent>
      </Popup>

      {openSendEmail && selectedTemplate && (
        <ManageSendEmail
          open={openSendEmail}
          onHide={closeHandlerView}
          selectedSms={selectedTemplate}
          title="Send Email"
        />
      )}
    </>
  );
};

export default ManageEmailTemplate;
